import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
} from 'chart.js';
import { aiService, AIStats } from '../../../services/aiService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
);

const AIStatsComponent: React.FC = () => {
  const [stats, setStats] = useState<AIStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
    // 每分鐘刷新一次統計數據
    const interval = setInterval(loadStats, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const data = await aiService.getStats();
      setStats(data);
      setError(null);
    } catch (err) {
      setError('無法載入統計數據');
      console.error('載入統計數據失敗:', err);
      // 設置默認數據以防止運行時錯誤
      setStats({
        summary: {
          totalDetections: 0,
          activeAlerts: 0,
          resolvedToday: 0,
          falsePositives: 0,
          avgResponseTime: 0
        },
        byType: {},
        bySeverity: {},
        byCamera: [],
        timeline: [],
        performance: {
          avgProcessingTime: 0,
          successRate: 0,
          errorRate: 0
        }
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <Alert variant="danger">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error || '無法載入統計數據'}
      </Alert>
    );
  }

  // 檢測類型分布圖表數據
  const typeChartData = {
    labels: Object.keys(stats?.byType || {}).map(type => {
          const typeNames: Record<string, string> = {
            abnormal_behavior: '異常行為',
            fire_smoke: '火災煙霧',
            fall_detection: '跌倒檢測',
            vehicle_collision: '車輛碰撞'
          };
          return typeNames[type] || type;
        }),
    datasets: [
      {
        data: Object.values(stats?.byType || {}).map(item => item.count),
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
        ],
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  };

  // 嚴重程度分布圖表數據
  const severityChartData = {
    labels: ['嚴重', '高', '中', '低'],
    datasets: [
      {
        label: '檢測數量',
        data: [
          stats.bySeverity?.critical?.count || 0,
          stats.bySeverity?.high?.count || 0,
          stats.bySeverity?.medium?.count || 0,
          stats.bySeverity?.low?.count || 0,
        ],
        backgroundColor: [
          '#dc3545',
          '#fd7e14',
          '#ffc107',
          '#20c997',
        ],
      },
    ],
  };

  // 時間線圖表數據
  const timelineChartData = {
    labels: (stats.timeline || []).map(item => item.time),
    datasets: [
      {
        label: '檢測數量',
        data: (stats.timeline || []).map(item => item.count),
        borderColor: '#0d6efd',
        backgroundColor: 'rgba(13, 110, 253, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
    },
  };

  return (
    <>
      {/* 統計卡片 */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="stats-card h-100">
            <Card.Body className="text-center">
              <div className="display-6 text-primary mb-2">
                <i className="fas fa-search"></i>
              </div>
              <h3 className="mb-1">{stats.summary?.totalDetections || 0}</h3>
              <p className="text-muted mb-0">總檢測次數</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stats-card h-100">
            <Card.Body className="text-center">
              <div className="display-6 text-warning mb-2">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h3 className="mb-1">{stats.summary?.activeAlerts || 0}</h3>
              <p className="text-muted mb-0">活躍警報</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stats-card h-100">
            <Card.Body className="text-center">
              <div className="display-6 text-success mb-2">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3 className="mb-1">{stats.summary?.resolvedToday || 0}</h3>
              <p className="text-muted mb-0">今日已處理</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stats-card h-100">
            <Card.Body className="text-center">
              <div className="display-6 text-info mb-2">
                <i className="fas fa-clock"></i>
              </div>
              <h3 className="mb-1">{stats.summary?.avgResponseTime || 0}s</h3>
              <p className="text-muted mb-0">平均響應時間</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 圖表區域 */}
      <Row>
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-pie me-2"></i>
                檢測類型分布
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Doughnut data={typeChartData} options={chartOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-bar me-2"></i>
                嚴重程度分布
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Bar data={severityChartData} options={chartOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={8} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-line me-2"></i>
                檢測趨勢
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Line data={timelineChartData} options={chartOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-video me-2"></i>
                攝像頭狀態
              </h5>
            </Card.Header>
            <Card.Body>
              {(stats.byCamera || []).map((camera, index) => (
                <div key={camera.cameraId} className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <div className="fw-bold">{camera.name}</div>
                    <small className="text-muted">檢測: {camera.count} 次</small>
                  </div>
                  <div className="text-end">
                    <div className={`camera-status ${camera.uptime > 95 ? 'online' : 'offline'}`}>
                      <i className="fas fa-circle me-1"></i>
                      {camera.uptime.toFixed(1)}%
                    </div>
                  </div>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 性能指標 */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-tachometer-alt me-2"></i>
                系統性能
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4} className="text-center">
                  <div className="display-6 text-info mb-2">
                    {stats.performance?.avgProcessingTime || 0}ms
                  </div>
                  <p className="text-muted mb-0">平均處理時間</p>
                </Col>
                <Col md={4} className="text-center">
                  <div className="display-6 text-success mb-2">
                    {stats.performance?.successRate || 0}%
                  </div>
                  <p className="text-muted mb-0">成功率</p>
                </Col>
                <Col md={4} className="text-center">
                  <div className="display-6 text-danger mb-2">
                    {stats.performance?.errorRate || 0}%
                  </div>
                  <p className="text-muted mb-0">錯誤率</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default AIStatsComponent;